import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/api';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

function TopBar({ selectedCorp }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const { logout, token, getUserRoles } = useAuth();
  const navigate = useNavigate();

  const parseJwt = (t) => { try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; } };
  const tokenData = token ? parseJwt(token) : null;
  const givenName = tokenData?.given_name || '';
  const familyName = tokenData?.family_name || '';
  const sub = tokenData?.sub || '';
  const initials = (givenName && familyName)
    ? `${givenName.charAt(0)}${familyName.charAt(0)}`.toUpperCase()
    : sub ? sub.charAt(0).toUpperCase() : 'U';
  const fullName = [givenName, familyName].filter(Boolean).join(' ') || sub || 'User';
  const roles = getUserRoles();
  const roleLabel = roles.includes('super') ? 'Super Admin'
    : roles.includes('admin') ? 'Admin'
      : roles.includes('employer') ? 'Employer'
        : roles.includes('employee') ? 'Employee'
          : 'User';

  const handleLogout = () => {
    try { window.localStorage.clear(); window.sessionStorage && window.sessionStorage.clear(); } catch (_) { }
    logout();
    navigate('/on/login');
  };

  useEffect(() => {
    const loadLogo = async () => {
      if (!selectedCorp?.id) { setLogoUrl(null); return; }
      try {
        const response = await fetchWithAuth(`/corps/${selectedCorp.id}/logo`, { headers: { Accept: 'image/*' } });
        if (!response.ok) { setLogoUrl(null); return; }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setLogoUrl(url);
        setCurrentLogoUrl(url);
      } catch { setLogoUrl(null); }
    };
    loadLogo();
    return () => { if (currentLogoUrl) URL.revokeObjectURL(currentLogoUrl); };
  }, [selectedCorp]);

  return (
    <header style={{
      background: '#ffffff',
      height: '60px',
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      zIndex: 100,
      flexShrink: 0,
      borderBottom: '1px solid #e2e8f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>

      {/* Left: Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {logoUrl ? (
          <img src={logoUrl} alt="logo"
            style={{ height: '32px', width: '32px', objectFit: 'contain', borderRadius: '7px', border: '1px solid #e2e8f0' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '32px', height: '32px', borderRadius: '7px', flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '14px', fontWeight: '800', color: 'white',
          }}>N</div>
        )}
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', letterSpacing: '0.1px', lineHeight: 1.2 }}>
            {selectedCorp ? selectedCorp.name : 'One Nexus'}
          </div>
        </div>
      </div>

      {/* Right: User + Logout */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '5px 12px 5px 5px', borderRadius: '40px',
          background: '#f8fafc', border: '1px solid #e2e8f0',
        }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: '700', color: 'white',
          }}>{initials}</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', lineHeight: 1.2 }}>{fullName}</div>
            <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '500' }}>{roleLabel}</div>
          </div>
        </div>

        <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '7px 14px', borderRadius: '8px',
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#dc2626', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
            transition: 'all 0.15s ease',
          }}
          onMouseOver={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </button>
      </div>
    </header>
  );
}

export default TopBar;
